"use strict";
/**
 * 平台总览：超管看"全平台现在什么情况"。
 *
 * 这份契约只服务一个页面（控制台「平台总览」），但类型与汇总逻辑放这里，
 * 是因为三个数字必须**只有一个出处**：租户数、站点数、月收入。
 * 页面里各写一遍 reduce，第一个改动之后三处就会开始互相矛盾 ——
 * 而这种矛盾不会报错，只会在某次向老板汇报时报出两个不同的数。
 *
 * 报表的每一列都对应一个运营问题：
 *   - 什么时候注册的、多少天了 → 这个客户是不是刚进来没人管
 *   - 几个站点、几个已发布 → 是不是只开通了没干活（开通≠上线）
 *   - 订阅状态、到期日 → 下周会不会掉
 *   - 累计付了多少 → 这个客户值不值得投入客服时间
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ATTENTION_WINDOW_DAYS = exports.TENANT_STAGES = void 0;
exports.trialDaysLeft = trialDaysLeft;
exports.tenantStage = tenantStage;
exports.ageInDays = ageInDays;
exports.summarizePlatform = summarizePlatform;
exports.formatDateTime = formatDateTime;
exports.yuan = yuan;
exports.csvCell = csvCell;
exports.platformRowsToCsv = platformRowsToCsv;
exports.TENANT_STAGES = [
    /** 租户本身被停用/终止：整站下线，与账单无关 */
    'tenant_suspended',
    /** 建了租户但一条订阅都没有：计费从没跑起来，金额一律按 0 算 */
    'no_subscription',
    'trialing',
    'active',
    'past_due',
    'suspended',
    /** 已取消 / 已终止：不再续费，但数据还在保留期内 */
    'ended',
];
const DAY_MS = 24 * 60 * 60 * 1000;
/** 跟进窗口。7 天是"还来得及打一个电话"的长度，再短就只能等掉站 */
exports.ATTENTION_WINDOW_DAYS = 7;
/** 只有这几种阶段谈得上"快要到期"。取消的与无订阅的不算 */
const DUE_SOON_STAGES = new Set([
    'trialing',
    'active',
    'past_due',
]);
/**
 * 距到期还有几天（向上取整）。
 *
 * `null` 表示没有到期日。负数表示已经过期 —— 保留符号而不是截断成 0，
 * 因为"已经过期 5 天"和"今天到期"要做的动作不一样。
 */
function trialDaysLeft(row, now = new Date()) {
    if (!row.subPeriodEnd)
        return null;
    const end = Date.parse(row.subPeriodEnd);
    if (Number.isNaN(end))
        return null;
    return Math.ceil((end - now.getTime()) / DAY_MS);
}
/** 订阅已到期但状态还停在 trialing / active 的，仍然按"已过期"处理 */
function expired(daysLeft) {
    return daysLeft !== null && daysLeft <= 0;
}
/**
 * 把一行数据翻译成"要做什么"。
 *
 * 判据顺序有讲究：**租户被停用优先于账单状态**。一个租户被平台停掉
 * （违规、滥用）时，他的订阅可能还是 active —— 先看账单会显示"付费中"，
 * 让人以为一切正常。
 */
function tenantStage(row, now = new Date()) {
    const daysLeft = trialDaysLeft(row, now);
    if (row.tenantStatus !== 'active') {
        return {
            stage: 'tenant_suspended',
            label: row.tenantStatus === 'terminated' ? '租户已终止' : '租户已停用',
            tone: 'danger',
            daysLeft,
            needsAttention: true,
        };
    }
    const status = row.subStatus;
    if (!status) {
        return {
            stage: 'no_subscription',
            label: '无订阅记录',
            tone: 'warn',
            daysLeft,
            needsAttention: true,
        };
    }
    switch (status) {
        case 'trialing': {
            const left = expired(daysLeft) ? '试用已过期' : `试用剩 ${daysLeft} 天`;
            return {
                stage: 'trialing',
                label: left,
                // 试用过期还没转付费 = 随时会掉站，按 danger 显示
                tone: expired(daysLeft) ? 'danger' : 'warn',
                daysLeft,
                needsAttention: daysLeft !== null && daysLeft <= exports.ATTENTION_WINDOW_DAYS,
            };
        }
        case 'active': {
            return {
                stage: 'active',
                label: row.subCancelAtPeriodEnd ? '付费中（期末不续）' : '付费中',
                // 到期不续的客户仍然是付费客户，只是别把他当续费目标了
                tone: row.subCancelAtPeriodEnd ? 'warn' : 'success',
                daysLeft,
                needsAttention: row.subCancelAtPeriodEnd || (daysLeft !== null && daysLeft <= exports.ATTENTION_WINDOW_DAYS),
            };
        }
        case 'past_due':
            return { stage: 'past_due', label: '欠费', tone: 'danger', daysLeft, needsAttention: true };
        case 'suspended':
            return { stage: 'suspended', label: '已停站', tone: 'danger', daysLeft, needsAttention: true };
        case 'canceled':
        case 'terminated':
            return { stage: 'ended', label: '已取消', tone: 'neutral', daysLeft, needsAttention: false };
    }
}
/** 注册至今多少天。0 表示今天刚注册 */
function ageInDays(createdAt, now = new Date()) {
    const t = Date.parse(createdAt);
    if (Number.isNaN(t))
        return null;
    return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}
/**
 * 汇总。
 *
 * `now` 可注入：所有断言都不该依赖"今天是几号"，否则测试会在某个
 * 时刻突然变红，而看的人第一反应是"代码坏了"。
 */
function summarizePlatform(rows, now = new Date()) {
    const summary = {
        tenantCount: rows.length,
        activeTenantCount: 0,
        siteCount: 0,
        liveSiteCount: 0,
        domainCount: 0,
        trialingCount: 0,
        payingCount: 0,
        pastDueCount: 0,
        noSubscriptionCount: 0,
        mrrCents: 0,
        paidTotalCents: 0,
        dueSoonCount: 0,
        needsAttentionCount: 0,
        newLast30Days: 0,
    };
    for (const row of rows) {
        if (row.tenantStatus === 'active')
            summary.activeTenantCount += 1;
        summary.siteCount += row.siteCount;
        summary.liveSiteCount += row.liveSiteCount;
        summary.domainCount += row.domainCount;
        summary.mrrCents += row.mrrCents;
        summary.paidTotalCents += row.paidCents;
        const view = tenantStage(row, now);
        if (view.needsAttention)
            summary.needsAttentionCount += 1;
        /**
         * 「7 天内到期」只对**还活着**的订阅有意义。
         *
         * 已取消的、没有订阅记录的，到期日只是历史残留 —— 把它们算进"即将到期"
         * 会让这个数字虚高，而超管看到的第一反应是"这么多人要掉了"。
         */
        if (DUE_SOON_STAGES.has(view.stage) &&
            view.daysLeft !== null &&
            view.daysLeft <= exports.ATTENTION_WINDOW_DAYS) {
            summary.dueSoonCount += 1;
        }
        switch (view.stage) {
            case 'trialing':
                summary.trialingCount += 1;
                break;
            case 'active':
                summary.payingCount += 1;
                break;
            case 'past_due':
                summary.pastDueCount += 1;
                break;
            case 'no_subscription':
                summary.noSubscriptionCount += 1;
                break;
            default:
                break;
        }
        const age = ageInDays(row.createdAt, now);
        if (age !== null && age <= 30)
            summary.newLast30Days += 1;
    }
    return summary;
}
const pad = (n) => String(n).padStart(2, '0');
/** `2026-10-28 16:07`。报表要能跟客户的话对上，所以写绝对时间不写"3 天前" */
function formatDateTime(iso) {
    if (!iso)
        return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
/** 分 → 元，不带货币符号（CSV 里靠列名说明币种，不要在每格重复） */
function yuan(cents) {
    return (cents / 100).toFixed(2);
}
/**
 * CSV 单元格转义：含分隔符/引号/换行时必须整体加引号，内部引号双写。
 *
 * 导出给 `leads.ts` 用：询盘的导出（邮箱、留言）比这张表更容易出现逗号与换行，
 * 两处各写一份迟早会在"引号双写"这一条上分叉，导出后 Excel 直接串列。
 */
function csvCell(value) {
    const text = value === null || value === undefined ? '' : String(value);
    if (/[",\r\n]/.test(text))
        return `"${text.replaceAll('"', '""')}"`;
    return text;
}
/**
 * 导出 CSV（UTF-8，带 BOM 由调用方加 —— 不加的话 Excel 打开是乱码，
 * 而超管导出这份表十有八九是要发给别人的）。
 *
 * 列的顺序刻意让"要打电话的人"排在前面：租户、所有者、状态、到期。
 * 站点数与商品数属于核对信息，放后面。
 */
function platformRowsToCsv(rows, now = new Date()) {
    const header = [
        '租户',
        '站点标识',
        '所有者邮箱',
        '注册时间',
        '已注册天数',
        '状态',
        '套餐',
        '本期到期',
        '剩余天数',
        '站点数',
        '在线站点',
        '域名数',
        '已发布页面',
        '上架商品',
        '成员数',
        '累计实付(元)',
        '月度收入(元)',
        '最近付款',
        '最后活跃',
    ];
    const lines = rows.map((row) => {
        const view = tenantStage(row, now);
        return [
            row.name,
            row.slug,
            row.ownerEmail,
            formatDateTime(row.createdAt),
            ageInDays(row.createdAt, now),
            view.label,
            row.planName,
            formatDateTime(row.subPeriodEnd),
            view.daysLeft,
            row.siteCount,
            row.liveSiteCount,
            row.domainCount,
            row.publishedPages,
            row.activeProducts,
            row.memberCount,
            yuan(row.paidCents),
            yuan(row.mrrCents),
            formatDateTime(row.lastPaidAt),
            formatDateTime(row.lastActivityAt),
        ]
            .map(csvCell)
            .join(',');
    });
    // 行尾用 \r\n：Excel 对纯 \n 的容忍度不稳，WPS 更是会整表挤成一行
    return [header.map(csvCell).join(','), ...lines].join('\r\n');
}
//# sourceMappingURL=platform.js.map